'use client';

import { useState } from 'react';
import YouTube from 'react-youtube';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useSession } from 'next-auth/react';
import { Calendar } from 'lucide-react';
import { Eye } from 'lucide-react';
import { format } from 'date-fns';
import { CheckCircle2 } from 'lucide-react';
import { Book } from 'lucide-react';
import { Play } from 'lucide-react';
import Link from 'next/link';
import { LogoRegister, LogoSlogan, LogoText } from '@/components/common';
import CommonNav from '@/components/layouts/CommonNav';
import { LogoLinks } from '@/components/common/smartguy-logo';

export default function VideoPage({ videoData }) {
  const { video } = videoData || {};
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isWatched, setIsWatched] = useState(video?.watched || false);
  const { data: session } = useSession();
  const token = session?.accessToken;

  const opts = {
    width: '100%',
    height: '100%',
    playerVars: {
      autoplay: 0,
      rel: 0,
    },
  };

  if (!video) {
    return <div>Video not found</div>;
  }

  const storeWatchHistory = async (id) => {
    try {
      await fetch(`${process.env.NEXT_PUBLIC_API_URL}/video-watch-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          videoId: id,
          videoWatchType: 'assessment',
        }),
      });
    } catch (error) {
      console.error('Error storing watch history:', error);
    }
  };

  const deleteWatchHistory = async (id) => {
    try {
      await fetch(`${process.env.NEXT_PUBLIC_API_URL}/video-watch-history/${id}?type=assessment`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Error deleting watch history:', error);
    }
  };

  const handleVideoWatched = async (checked) => {
    setIsWatched(checked);
    if (checked) {
      await storeWatchHistory(video._id);
    } else {
      await deleteWatchHistory(video._id);
    }
  };

  return (
    <div className="container mx-auto max-w-7xl px-3 py-6 sm:px-4 sm:py-8">
      <div>
        <div className="space-y-4">
          <div className="flex flex-1 flex-col items-center justify-center pt-4">
            <Link href="/" className="flex flex-col items-center justify-center">
              <div className="flex items-center">
                <LogoText className="text-3xl" />
                <LogoRegister />
              </div>
              <LogoSlogan className="me-4 text-center text-[.7rem]" />
            </Link>

            <LogoLinks />
          </div>
          <div>
            <CommonNav />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 pt-6 lg:gap-8">
          <div className="space-y-6 lg:col-span-2">
            <Card className="overflow-hidden rounded-xl border-none bg-white py-0 shadow-lg">
              <CardContent className="p-0">
                <div className="bg-muted relative aspect-video w-full">
                  {!isVideoReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <Skeleton className="h-full w-full" />
                      <div className="text-muted-foreground absolute animate-pulse text-sm sm:text-lg">
                        Loading video...
                      </div>
                    </div>
                  )}

                  <YouTube
                    videoId={video.videoId}
                    className={`h-full w-full ${!isVideoReady ? 'invisible' : ''}`}
                    opts={opts}
                    iframeClassName="w-full h-full"
                    onReady={() => setIsVideoReady(true)}
                    onError={(e) => console.error('YouTube error:', e)}
                    onPlay={() => {
                      if (!isWatched) {
                        setIsWatched(true);
                        storeWatchHistory(video._id);
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-bold text-gray-900 capitalize sm:text-3xl md:text-4xl">
                  {video.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Eye className="size-5 text-gray-500" />
                    <span>{video.views || 0} views</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="size-4 text-gray-500" />
                    <span>
                      {video.youtubePublishedAt
                        ? format(new Date(video.youtubePublishedAt), 'dd MMM, yyyy')
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {video.description && (
                <Card className="border border-gray-100 bg-white py-0">
                  <CardContent className="p-4">
                    <h3 className="mb-2 font-semibold text-gray-900">Description</h3>
                    <p className="text-sm leading-relaxed text-gray-700 sm:text-base">
                      {video.description}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white p-4">
              <Label
                htmlFor="watchedVideo"
                className="cursor-pointer text-sm font-medium text-gray-700 sm:text-base"
              >
                After watching the video, check the box to mark it as complete.
              </Label>

              <Checkbox
                id="watchedVideo"
                checked={isWatched}
                onCheckedChange={handleVideoWatched}
                className="h-5 w-5 cursor-pointer"
              />
            </div> */}
            </div>
          </div>

          {/* Sidebar - Additional Information */}
          {/* <div className="space-y-6">
          <Card className="from-primary/5 to-primary/10 border-none bg-gradient-to-br">
            <CardContent className="p-6">
              <h2 className="mb-4 text-xl font-semibold text-gray-900">Improve Your Personality</h2>
              <p className="text-sm leading-relaxed text-gray-700">
                After watching this video, enhance your personality traits by:
              </p>
              <ul className="mt-4 space-y-3">
                <li className="">
                  <Link
                    href={`/tasks/${videoData?.trait.slug}`}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle2 className="text-primary size-4" />
                    <span className="text-sm text-gray-600">Complete recommended tasks</span>
                  </Link>
                </li>
                <li className="flex items-center gap-2">
                  <Link
                    href={`/movies/${videoData?.trait.slug}`}
                    className="flex items-center gap-2"
                  >
                    <Play className="text-primary size-4" />
                    <span className="text-sm text-gray-600">Watch recommended movies</span>
                  </Link>
                </li>
                <li className="flex items-center gap-2">
                  <Link
                    href={`/readings/${videoData?.trait.slug}`}
                    className="flex items-center gap-2"
                  >
                    <Book className="text-primary size-4" />
                    <span className="text-sm text-gray-600">Reading recommended books</span>
                  </Link>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div> */}
        </div>
      </div>
    </div>
  );
}
